import React, { useRef } from 'react';

// Outillage de mise en page partage par les scenes de tournage (/video, /lick) :
// enveloppe editable (drag/zoom/crop persistes) et chargement assaini du
// layout. Ce ne sont pas des composants de l'app upScale.

export const ZERO_CROP = { l: 0, r: 0, t: 0, b: 0 };

// Id YouTube depuis youtu.be/<id>, watch?v=<id>, embed/<id>, shorts/<id>, ou
// l'id nu (les URL de scene sont souvent recopiees a la main).
export function youtubeId(url) {
  const s = String(url || '');
  if (/^[\w-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}

// Pas de la grille magnetique, en pixels de scene (la scene fait 1920x1080 a
// l'echelle 1:1, donc 1px client = 1px de scene).
export const SNAP_GRID = 40;

const snapTo = (v, g) => Math.round(v / g) * g;

// Assainit une entree de layout : uniquement x/y/s/crop, en nombres valides.
export function sanitizeBox(raw, fallback) {
  const num = (v, d) => (typeof v === 'number' && isFinite(v) ? v : d);
  const box = raw || {};
  const crop = box.crop || {};
  return {
    x: num(box.x, fallback.x),
    y: num(box.y, fallback.y),
    s: num(box.s, fallback.s),
    crop: {
      l: num(crop.l, 0),
      r: num(crop.r, 0),
      t: num(crop.t, 0),
      b: num(crop.b, 0),
    },
  };
}

export function loadLayout(storageKey, defaults) {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(storageKey));
  } catch {
    saved = null;
  }
  const out = {};
  for (const id of Object.keys(defaults)) {
    out[id] = sanitizeBox(saved?.[id], defaults[id]);
  }
  return out;
}

// Grille pointillee de la scene, visible seulement quand l'aimantation est
// active en mode edition. Purement decorative : jamais capturee au Render,
// puisque le mode edition est coupe avant l'enregistrement.
export const SnapGrid = ({ step = SNAP_GRID }) => (
  <div className="vp-snap-grid" style={{ backgroundSize: `${step}px ${step}px` }} />
);

// Enveloppe editable : transform x/y/scale (+ crop optionnel en clip-path)
// persiste. En mode edition : drag pour deplacer, poignee d'angle pour zoomer,
// poignees de bord pour cropper (applique en direct, cadre bleu aux bords).
export const EditableBox = ({ id, layout, editMode, onLayout, className = '', cropable = false, snap = 0, children }) => {
  const box = layout[id] || { x: 0, y: 0, s: 1 };
  const crop = box.crop || ZERO_CROP;
  const ref = useRef(null);

  const startDrag = (e, mode) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    const px = e.clientX;
    const py = e.clientY;
    const start = { x: box.x || 0, y: box.y || 0, s: box.s || 1, crop: { ...ZERO_CROP, ...crop } };

    // Geometrie de depart, en coordonnees de scene. L'origine du transform est
    // en haut a gauche : le coin haut gauche est l'ancre, il ne bouge pas avec
    // le zoom. w/h sont les dimensions locales (avant mise a l'echelle), et
    // rw/rh la distance de l'ancre au coin bas droit *visible* (crop deduit) :
    // c'est ce coin-la que la poignee de zoom suit et que la grille aimante.
    const el = ref.current;
    const stage = el?.closest('.video-stage');
    let geo = null;
    if (el && stage) {
      const r = el.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      const w = r.width / start.s;
      const h = r.height / start.s;
      const rw = w - start.crop.r;
      const rh = h - start.crop.b;
      if (rw > 0 && rh > 0) {
        geo = {
          left: r.left - sr.left,
          top: r.top - sr.top,
          w,
          h,
          rw,
          rh,
          sx: sr.left,
          sy: sr.top,
        };
      }
    }

    // Ecart entre le pointeur et le coin bas droit visible au moment du clic :
    // la poignee reste ainsi collee au curseur pendant tout le zoom.
    const grabX = geo ? px - geo.sx - (geo.left + geo.rw * start.s) : 0;

    // Pendant le drag, le panneau d'edition s'efface (voir .vp-dragging) :
    // rien ne masque la scene pendant qu'on ajuste.
    document.querySelector('.video-page')?.classList.add('vp-dragging');
    const onMove = (ev) => {
      const dx = ev.clientX - px;
      const dy = ev.clientY - py;
      if (mode === 'move') {
        let x = start.x + dx;
        let y = start.y + dy;
        if (snap && geo) {
          // On aimante le coin haut gauche visible (crop compris).
          const vl = geo.left + start.crop.l * start.s + dx;
          const vt = geo.top + start.crop.t * start.s + dy;
          x += snapTo(vl, snap) - vl;
          y += snapTo(vt, snap) - vt;
        }
        onLayout(id, { ...start, x, y });
      } else if (mode === 'scale') {
        let s;
        if (geo) {
          // Le coin bas droit visible suit le pointeur, puis s'aimante.
          let right = ev.clientX - geo.sx - grabX;
          if (snap) right = snapTo(right, snap);
          s = (right - geo.left) / geo.rw;
        } else {
          s = start.s * (1 + dx / 300);
        }
        onLayout(id, { ...start, s: Math.min(3, Math.max(0.25, s)) });
      } else {
        // dx/dy en coordonnees locales : l'element est mis a l'echelle par s
        const ldx = dx / start.s;
        const ldy = dy / start.s;
        const c = { ...start.crop };
        if (mode === 'crop-l') c.l = Math.max(0, start.crop.l + ldx);
        if (mode === 'crop-r') c.r = Math.max(0, start.crop.r - ldx);
        if (mode === 'crop-t') c.t = Math.max(0, start.crop.t + ldy);
        if (mode === 'crop-b') c.b = Math.max(0, start.crop.b - ldy);
        if (snap && geo) {
          // Aimante le bord visible (et non la valeur de crop) sur la grille.
          const s = start.s;
          if (mode === 'crop-l') c.l = Math.max(0, (snapTo(geo.left + c.l * s, snap) - geo.left) / s);
          if (mode === 'crop-t') c.t = Math.max(0, (snapTo(geo.top + c.t * s, snap) - geo.top) / s);
          if (mode === 'crop-r') {
            const right = snapTo(geo.left + (geo.w - c.r) * s, snap);
            c.r = Math.max(0, geo.w - (right - geo.left) / s);
          }
          if (mode === 'crop-b') {
            const bottom = snapTo(geo.top + (geo.h - c.b) * s, snap);
            c.b = Math.max(0, geo.h - (bottom - geo.top) / s);
          }
        }
        onLayout(id, { ...start, crop: c });
      }
    };
    const onUp = () => {
      document.querySelector('.video-page')?.classList.remove('vp-dragging');
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const hasCrop = cropable && (crop.l || crop.r || crop.t || crop.b);

  return (
    <div
      ref={ref}
      className={`vp-editable ${className}`}
      style={{
        transform: `translate(${box.x}px, ${box.y}px) scale(${box.s})`,
        clipPath: hasCrop
          ? `inset(${crop.t}px ${crop.r}px ${crop.b}px ${crop.l}px round 24px)`
          : undefined,
      }}
      onPointerDown={(e) => startDrag(e, 'move')}
    >
      {children}
      {editMode && (
        <div
          className="vp-handle"
          title="Zoom"
          style={cropable ? { right: crop.r + 4, bottom: crop.b + 4 } : undefined}
          onPointerDown={(e) => startDrag(e, 'scale')}
        />
      )}
      {editMode && cropable && (
        <>
          <div
            className="vp-crop-preview"
            style={{ left: crop.l, right: crop.r, top: crop.t, bottom: crop.b }}
          />
          <div className="vp-crop-handle vp-crop-l" style={{ left: crop.l + 4 }} onPointerDown={(e) => startDrag(e, 'crop-l')} />
          <div className="vp-crop-handle vp-crop-r" style={{ right: crop.r + 4 }} onPointerDown={(e) => startDrag(e, 'crop-r')} />
          <div className="vp-crop-handle vp-crop-t" style={{ top: crop.t + 4 }} onPointerDown={(e) => startDrag(e, 'crop-t')} />
          <div className="vp-crop-handle vp-crop-b" style={{ bottom: crop.b + 4 }} onPointerDown={(e) => startDrag(e, 'crop-b')} />
        </>
      )}
    </div>
  );
};
